import React, { useState, useEffect } from 'react';
import {
    Play, Square, RefreshCw, Search, Camera, MessageSquare,
    CheckCircle, XCircle, Wifi, WifiOff, Activity, Clock, Users,
    TrendingUp, AlertTriangle, Settings, BarChart3, MessageCircle,
    Zap, Eye, Volume2, VolumeX, Bot, User, ArrowUp, ArrowDown,
    ChevronRight, Bell, Shield, Sparkles, Timer, Target
} from 'lucide-react';
import useStore from '../store';

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, trend, color = 'blue', subtext }) => (
    <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50 hover:border-gray-600 transition-all">
        <div className="flex items-start justify-between">
            <div className={`p-2 rounded-lg bg-${color}-500/20`}>
                <Icon size={20} className={`text-${color}-400`} />
            </div>
            {trend !== undefined && (
                <div className={`flex items-center gap-1 text-xs ${trend >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {trend >= 0 ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                    {Math.abs(trend)}%
                </div>
            )}
        </div>
        <div className="mt-3">
            <div className="text-2xl font-bold text-white">{value}</div>
            <div className="text-xs text-gray-400 mt-1">{label}</div>
            {subtext && <div className="text-xs text-gray-500 mt-0.5">{subtext}</div>}
        </div>
    </div>
);

// Status Badge
const StatusBadge = ({ active, label }) => (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${active ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-gray-700 text-gray-400 border border-gray-600'
        }`}>
        <span className={`w-2 h-2 rounded-full ${active ? 'bg-green-400 animate-pulse' : 'bg-gray-500'}`} />
        {label}
    </div>
);

// Quick Action Button
const ActionButton = ({ icon: Icon, label, onClick, variant = 'default', loading, disabled }) => {
    const variants = {
        default: 'bg-gray-700 hover:bg-gray-600 text-gray-200',
        primary: 'bg-blue-600 hover:bg-blue-700 text-white',
        success: 'bg-green-600 hover:bg-green-700 text-white',
        danger: 'bg-red-600 hover:bg-red-700 text-white',
        warning: 'bg-orange-600 hover:bg-orange-700 text-white'
    };

    return (
        <button
            onClick={onClick}
            disabled={disabled || loading}
            className={`flex items-center gap-3 p-4 rounded-xl border border-gray-700/50 transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed ${variants[variant]}`}
        >
            <div className={`p-2 rounded-lg ${variant === 'default' ? 'bg-gray-600' : 'bg-white/10'}`}>
                <Icon size={20} className={loading ? 'animate-spin' : ''} />
            </div>
            <div className="text-left">
                <div className="font-medium">{label}</div>
            </div>
        </button>
    );
};

// Session Card
const SessionCard = ({ session, onReply }) => (
    <div className="bg-gray-800/30 rounded-lg p-3 border border-gray-700/30 hover:bg-gray-800/50 transition-all">
        <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center text-white font-bold">
                {session.name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                    <span className="font-medium text-sm text-gray-200 truncate">{session.name || '未知用户'}</span>
                    <span className="text-xs text-gray-500">{session.time || '刚刚'}</span>
                </div>
                <p className="text-xs text-gray-400 mt-1 truncate">{session.lastMessage || '暂无消息'}</p>
                <div className="flex items-center gap-2 mt-2">
                    {session.waiting && (
                        <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full">待回复</span>
                    )}
                    {session.priority && (
                        <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded-full">优先</span>
                    )}
                </div>
            </div>
        </div>
    </div>
);

const DoudianDashboard = () => {
    const { sendCommand, targetId, currentScreenshot, onlineTargets, logs } = useStore();
    const [autoReplyEnabled, setAutoReplyEnabled] = useState(false);
    const [aiModeEnabled, setAiModeEnabled] = useState(true);
    const [soundEnabled, setSoundEnabled] = useState(true);
    const [isLoading, setIsLoading] = useState(null);

    // Real stats from extension - updated via STATUS_RESULT messages
    const [stats, setStats] = useState({
        totalSessions: 0,
        waitingReply: 0,
        todayReplies: 0,
        avgResponseTime: '--',
        aiHandledPercent: 0
    });

    // Real sessions from extension
    const [sessions, setSessions] = useState([]);

    const connectedTarget = onlineTargets.find(t => t.id === targetId);

    // Listen for status updates from extension
    useEffect(() => {
        // Find the latest STATUS_RESULT log
        const statusLog = [...logs].reverse().find(log => log.type === 'STATUS_RESULT');

        if (statusLog?.payload) {
            const payload = statusLog.payload;

            // Update Auto Reply State
            if (payload.autoReplyEnabled !== undefined) {
                setAutoReplyEnabled(payload.autoReplyEnabled);
            }

            // Update Stats
            setStats(prev => ({
                ...prev,
                totalSessions: payload.totalSessions !== undefined ? payload.totalSessions : prev.totalSessions,
                waitingReply: payload.waitingReply !== undefined ? payload.waitingReply : prev.waitingReply,
                todayReplies: payload.todayReplies !== undefined ? payload.todayReplies : prev.todayReplies,
                avgResponseTime: payload.avgResponseTime || prev.avgResponseTime,
                aiHandledPercent: payload.aiHandledPercent !== undefined ? payload.aiHandledPercent : prev.aiHandledPercent
            }));

            // Update Sessions
            if (payload.sessions && Array.isArray(payload.sessions)) {
                setSessions(payload.sessions);
            }
        }
    }, [logs]);

    const handleToggleAutoReply = () => {
        if (!targetId) return;
        setIsLoading('autoReply');
        const command = autoReplyEnabled ? 'STOP_AUTO_REPLY' : 'START_AUTO_REPLY';
        sendCommand('COMMAND', { command, id: Date.now() });

        // Optimistic update
        setTimeout(() => {
            setIsLoading(null);
        }, 1000);
    };

    const handleRefreshStatus = () => {
        if (!targetId) return;
        setIsLoading('refresh');
        sendCommand('COMMAND', { command: 'GET_STATUS', id: Date.now() });
        setTimeout(() => setIsLoading(null), 1000);
    };

    const handleCaptureScreen = () => {
        if (!targetId) return;
        setIsLoading('capture');
        sendCommand('GET_SCREENSHOT', { id: Date.now() });
        setTimeout(() => setIsLoading(null), 2000);
    };

    // Auto-fetch status when target connects
    useEffect(() => {
        if (targetId) {
            handleRefreshStatus();
        }
    }, [targetId]);

    return (
        <div className="flex-1 bg-gray-900 overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 z-10 bg-gray-900/95 backdrop-blur-sm border-b border-gray-800 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center">
                            <MessageSquare size={24} className="text-white" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-white">抖店客服工作台</h1>
                            <div className="flex items-center gap-3 mt-1">
                                {connectedTarget ? (
                                    <>
                                        <StatusBadge active={true} label="已连接" />
                                        <span className="text-xs text-gray-400">
                                            {connectedTarget.metadata?.shopName || connectedTarget.metadata?.platform || '未知店铺'}
                                        </span>
                                    </>
                                ) : (
                                    <StatusBadge active={false} label="未连接" />
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setSoundEnabled(!soundEnabled)}
                            className="p-2 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors"
                        >
                            {soundEnabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
                        </button>
                        <button className="p-2 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors relative">
                            <Bell size={20} />
                            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
                        </button>
                        <button className="p-2 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors">
                            <Settings size={20} />
                        </button>
                    </div>
                </div>
            </div>

            <div className="p-6 space-y-6">
                {/* Stats Row */}
                <div className="grid grid-cols-5 gap-4">
                    <StatCard icon={Users} label="今日会话" value={stats.totalSessions} trend={0} color="blue" />
                    <StatCard icon={AlertTriangle} label="待回复" value={stats.waitingReply} color="red" subtext="需要处理" />
                    <StatCard icon={MessageCircle} label="今日回复" value={stats.todayReplies} trend={0} color="green" />
                    <StatCard icon={Timer} label="平均响应" value={stats.avgResponseTime} trend={0} color="purple" />
                    <StatCard icon={Bot} label="AI处理率" value={`${stats.aiHandledPercent}%`} trend={0} color="orange" />
                </div>

                {/* Main Content Grid */}
                <div className="grid grid-cols-3 gap-6">
                    {/* Left Column: Controls */}
                    <div className="space-y-6">
                        {/* Quick Actions */}
                        <div className="bg-gray-800/30 rounded-2xl p-5 border border-gray-700/50">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-sm font-bold text-gray-200 flex items-center gap-2">
                                    <Zap size={16} className="text-yellow-400" />
                                    快捷操作
                                </h2>
                                <button
                                    onClick={handleRefreshStatus}
                                    className="p-1.5 rounded-lg hover:bg-gray-700 text-gray-400"
                                >
                                    <RefreshCw size={14} className={isLoading === 'refresh' ? 'animate-spin' : ''} />
                                </button>
                            </div>

                            <div className="grid grid-cols-1 gap-3">
                                <ActionButton
                                    icon={autoReplyEnabled ? Square : Play}
                                    label={autoReplyEnabled ? '停止自动回复' : '启动自动回复'}
                                    variant={autoReplyEnabled ? 'danger' : 'success'}
                                    onClick={handleToggleAutoReply}
                                    loading={isLoading === 'autoReply'}
                                    disabled={!targetId}
                                />
                                <ActionButton
                                    icon={Camera}
                                    label="截取屏幕"
                                    variant="primary"
                                    onClick={handleCaptureScreen}
                                    loading={isLoading === 'capture'}
                                    disabled={!targetId}
                                />
                                <ActionButton
                                    icon={Search}
                                    label="诊断选择器"
                                    variant="default"
                                    onClick={() => sendCommand('COMMAND', { command: 'SCAN_SELECTORS', id: Date.now() })}
                                    disabled={!targetId}
                                />
                            </div>
                        </div>

                        {/* Mode Toggles */}
                        <div className="bg-gray-800/30 rounded-2xl p-5 border border-gray-700/50">
                            <h2 className="text-sm font-bold text-gray-200 flex items-center gap-2 mb-4">
                                <Settings size={16} className="text-blue-400" />
                                工作模式
                            </h2>
                            <div className="space-y-3">
                                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-xl">
                                    <div className="flex items-center gap-3">
                                        <Bot size={18} className="text-purple-400" />
                                        <div>
                                            <div className="text-sm font-medium text-gray-200">AI 模式</div>
                                            <div className="text-xs text-gray-500">自动生成回复建议</div>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setAiModeEnabled(!aiModeEnabled)}
                                        className={`w-12 h-6 rounded-full transition-colors ${aiModeEnabled ? 'bg-purple-600' : 'bg-gray-600'}`}
                                    >
                                        <span className={`block w-5 h-5 rounded-full bg-white shadow transform transition-transform ${aiModeEnabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                                    </button>
                                </div>

                                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-xl">
                                    <div className="flex items-center gap-3">
                                        <Shield size={18} className="text-green-400" />
                                        <div>
                                            <div className="text-sm font-medium text-gray-200">安全模式</div>
                                            <div className="text-xs text-gray-500">敏感词过滤</div>
                                        </div>
                                    </div>
                                    <button className="w-12 h-6 rounded-full bg-green-600">
                                        <span className="block w-5 h-5 rounded-full bg-white shadow translate-x-6" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Center Column: Live Preview */}
                    <div className="bg-gray-800/30 rounded-2xl p-5 border border-gray-700/50">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-sm font-bold text-gray-200 flex items-center gap-2">
                                <Eye size={16} className="text-cyan-400" />
                                实时预览
                            </h2>
                            <button
                                onClick={handleCaptureScreen}
                                disabled={!targetId}
                                className="text-xs text-blue-400 hover:text-blue-300 disabled:opacity-50"
                            >
                                刷新
                            </button>
                        </div>
                        <div className="aspect-[4/3] bg-gray-900 rounded-xl overflow-hidden flex items-center justify-center border border-gray-700/50">
                            {currentScreenshot ? (
                                <img src={currentScreenshot} alt="Live Preview" className="w-full h-full object-contain" />
                            ) : (
                                <div className="text-center text-gray-500">
                                    <Camera size={48} className="mx-auto mb-3 opacity-30" />
                                    <p className="text-sm">点击截图查看预览</p>
                                    <p className="text-xs text-gray-600 mt-1">连接扩展程序后可用</p>
                                </div>
                            )}
                        </div>

                        {/* Quick Stats under preview */}
                        <div className="grid grid-cols-3 gap-2 mt-4">
                            <div className="text-center p-2 bg-gray-800/50 rounded-lg">
                                <div className="text-lg font-bold text-green-400">{autoReplyEnabled ? 'ON' : 'OFF'}</div>
                                <div className="text-xs text-gray-500">自动回复</div>
                            </div>
                            <div className="text-center p-2 bg-gray-800/50 rounded-lg">
                                <div className="text-lg font-bold text-blue-400">{targetId ? '在线' : '离线'}</div>
                                <div className="text-xs text-gray-500">连接状态</div>
                            </div>
                            <div className="text-center p-2 bg-gray-800/50 rounded-lg">
                                <div className="text-lg font-bold text-purple-400">{aiModeEnabled ? 'AI' : '手动'}</div>
                                <div className="text-xs text-gray-500">工作模式</div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Sessions */}
                    <div className="bg-gray-800/30 rounded-2xl p-5 border border-gray-700/50">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-sm font-bold text-gray-200 flex items-center gap-2">
                                <MessageCircle size={16} className="text-orange-400" />
                                最近会话
                            </h2>
                            <span className="text-xs text-gray-500">{sessions.length} 条</span>
                        </div>
                        <div className="space-y-2 max-h-[400px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700">
                            {sessions.length === 0 ? (
                                <div className="text-center py-8 text-gray-500 text-xs">
                                    暂无会话数据
                                </div>
                            ) : (
                                sessions.map((session, idx) => (
                                    <SessionCard key={session.id || idx} session={session} />
                                ))
                            )}
                        </div>

                        <button className="w-full mt-4 p-2 text-center text-xs text-blue-400 hover:text-blue-300 hover:bg-gray-800/50 rounded-lg transition-colors">
                            查看全部会话 <ChevronRight size={14} className="inline" />
                        </button>
                    </div>
                </div>

                {/* Connection Info Footer */}
                {connectedTarget && (
                    <div className="bg-gray-800/30 rounded-xl p-4 border border-gray-700/50">
                        <div className="flex items-center justify-between text-xs text-gray-400">
                            <div className="flex items-center gap-6">
                                <span><strong className="text-gray-300">设备ID:</strong> {targetId?.substring(0, 20)}...</span>
                                <span><strong className="text-gray-300">IP:</strong> {connectedTarget.metadata?.clientIp || 'Unknown'}</span>
                                <span><strong className="text-gray-300">位置:</strong> {connectedTarget.metadata?.ipLocation || 'Unknown'}</span>
                                <span><strong className="text-gray-300">店铺:</strong> {connectedTarget.metadata?.shopName || 'Unknown'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                                <span>实时同步中</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DoudianDashboard;
